import React, {
  PropsWithChildren,
  useCallback,
  useEffect,
  useReducer,
} from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useSorting } from "@common/hooks/useSorting";

export type Actions<T> = "reset" | IListData<T> | SortChangeAction<T>;

export interface IListData<T> {
  total?: number;
  list?: T[];
  page?: number;
}

export interface SortChangeAction<T> {
  sort: Sort<T>;
}

export interface IListContext<T, A = Actions<T>> extends IListData<T> {
  dispatch: (action: A) => void;
  sort: Sort<T>;
}

export const initialContext: IListContext<any> = {
  dispatch: () => {},
  sort: {
    column: "name",
    direction: "asc",
  },
};

const isSortAction = <T,>(
  action: Actions<T>
): action is SortChangeAction<T> => {
  return (action as SortChangeAction<T>).sort !== undefined;
};

export const ListProvider = <T,>(
  props: PropsWithChildren<{
    context: React.Context<IListContext<T>>;
    initialState?: IListContext<T>;
  }>
) => {
  const { children, initialState = initialContext, context: Context } = props;
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [sort, setSort] = useSorting<T>();
  const reducer = useCallback(
    (state: IListContext<T>, action: Actions<T>) => {
      if (action === "reset") {
        return { dispatch: state.dispatch, sort: state.sort, page: 1 };
      }
      if (isSortAction(action)) {
        setSort(action.sort);
        return { ...state, ...action };
      }
      const newState = { ...state, ...action };
      return newState;
    },
    [location, searchParams, setSort]
  );

  const initialSorting: Sort<any> = sort || initialState.sort;

  const [state, dispatch] = useReducer(reducer, {
    ...initialState,
    sort: initialSorting,
    page: (location.state as { page?: number })?.page ?? 1,
  });

  useEffect(() => {
    const to = window.location.pathname + window.location.search;

    navigate(to, {
      replace: true,
      state: { ...(location.state as any), page: state.page },
    });
  }, [state.page]);

  return (
    <Context.Provider value={{ ...state, dispatch }}>
      {children}
    </Context.Provider>
  );
};
